import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Mail, Phone, ArrowRight, CheckCircle2 } from 'lucide-react';

const serviceAreas = [
  'Financial Control & Infrastructure',
  'Strategic Financial Planning',
  'Ongoing Accounting Operations',
  'Reporting & Data Visibility',
  'Automation & Intelligent Operations',
  'Payroll & Workforce Compliance',
  'Bankability & Capital Readiness',
  'Portfolio Support',
  'General Inquiry',
];

export default function Contact() {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    service: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    await new Promise(r => setTimeout(r, 800));
    setLoading(false);
    setSubmitted(true);
  };

  return (
    <div>
      {/* ── Hero ─────────────────────────────────── */}
      <section className="grid-bg pt-32 pb-16" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-[#0ABFBC] text-[10px] font-bold uppercase tracking-[0.25em] mb-4">
              Contact Boston Federal
            </p>
            <h1 className="text-5xl sm:text-6xl font-bold text-[#1A2234] leading-[1.05] tracking-tight mb-4">
              Let's Build Something<br />
              <span className="text-[#0ABFBC]">Together</span>
            </h1>
            <p className="text-lg text-[#475569] max-w-xl leading-relaxed">
              Whether you're looking to improve financial control, tackle complex compliance, or
              build the infrastructure for capital access — we'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Contact Content ───────────────────────── */}
      <section className="bg-white py-16 pb-24" data-testid="contact-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">

            {/* Left: Info */}
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-2"
            >
              <h2 className="text-2xl font-bold text-[#1A2234] mb-4">Get in Touch</h2>
              <p className="text-[#475569] leading-relaxed mb-8 text-sm">
                Boston Federal works with businesses across industries to build the financial
                infrastructure, compliance systems, and operational control they need to grow.
                Reach out — we'll find the right path forward together.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 rounded-lg bg-[#0ABFBC]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <MapPin size={16} strokeWidth={1.5} className="text-[#0ABFBC]" />
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#1A2234] mb-1">Office</div>
                    <div className="text-[#475569] text-sm">Boston, Massachusetts</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 rounded-lg bg-[#0ABFBC]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Mail size={16} strokeWidth={1.5} className="text-[#0ABFBC]" />
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#1A2234] mb-1">Email</div>
                    <a
                      href="mailto:info@bostonfederal.com"
                      className="text-[#475569] text-sm hover:text-[#0ABFBC] transition-colors"
                    >
                      info@bostonfederal.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 rounded-lg bg-[#0ABFBC]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Phone size={16} strokeWidth={1.5} className="text-[#0ABFBC]" />
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#1A2234] mb-1">Phone</div>
                    <a
                      href="tel:+16170000000"
                      className="text-[#475569] text-sm hover:text-[#0ABFBC] transition-colors"
                    >
                      (617) 000-0000
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right: Form */}
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-3"
            >
              {submitted ? (
                <div className="text-center py-16" data-testid="contact-success">
                  <div className="w-16 h-16 rounded-full bg-[#0ABFBC]/10 flex items-center justify-center mx-auto mb-5">
                    <CheckCircle2 size={32} strokeWidth={1.5} className="text-[#0ABFBC]" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#1A2234] mb-2">Message Sent</h3>
                  <p className="text-[#475569] mb-6">
                    Thank you for reaching out. We'll be in touch within one business day.
                  </p>
                  <Link
                    to="/"
                    className="text-[#0ABFBC] font-semibold text-sm hover:underline"
                  >
                    Return to Home
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#1A2234] mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={form.firstName}
                        onChange={handleChange}
                        required
                        placeholder="John"
                        data-testid="contact-first-name"
                        className="w-full border border-slate-200 rounded-md px-4 py-3 text-[#1A2234] text-sm placeholder-slate-300 focus:outline-none focus:border-[#0ABFBC] focus:ring-1 focus:ring-[#0ABFBC]/30 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#1A2234] mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={form.lastName}
                        onChange={handleChange}
                        required
                        placeholder="Smith"
                        data-testid="contact-last-name"
                        className="w-full border border-slate-200 rounded-md px-4 py-3 text-[#1A2234] text-sm placeholder-slate-300 focus:outline-none focus:border-[#0ABFBC] focus:ring-1 focus:ring-[#0ABFBC]/30 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#1A2234] mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      placeholder="Your Company Name"
                      data-testid="contact-company"
                      className="w-full border border-slate-200 rounded-md px-4 py-3 text-[#1A2234] text-sm placeholder-slate-300 focus:outline-none focus:border-[#0ABFBC] focus:ring-1 focus:ring-[#0ABFBC]/30 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#1A2234] mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder="john@company.com"
                      data-testid="contact-email"
                      className="w-full border border-slate-200 rounded-md px-4 py-3 text-[#1A2234] text-sm placeholder-slate-300 focus:outline-none focus:border-[#0ABFBC] focus:ring-1 focus:ring-[#0ABFBC]/30 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#1A2234] mb-2">
                      How Can We Help?
                    </label>
                    <select
                      name="service"
                      value={form.service}
                      onChange={handleChange}
                      data-testid="contact-service-area"
                      className="w-full border border-slate-200 rounded-md px-4 py-3 text-sm text-[#1A2234] focus:outline-none focus:border-[#0ABFBC] focus:ring-1 focus:ring-[#0ABFBC]/30 transition-colors bg-white"
                    >
                      <option value="">Select a service area...</option>
                      {serviceAreas.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#1A2234] mb-2">
                      Tell Us About Your Business
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      rows={5}
                      placeholder="Share some background on your business and what you're looking to accomplish..."
                      data-testid="contact-message"
                      className="w-full border border-slate-200 rounded-md px-4 py-3 text-[#1A2234] text-sm placeholder-slate-300 focus:outline-none focus:border-[#0ABFBC] focus:ring-1 focus:ring-[#0ABFBC]/30 transition-colors resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-btn"
                    className="bg-[#0ABFBC] text-white w-full py-3.5 rounded-md font-semibold hover:bg-[#09A09D] transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
                  >
                    {loading ? 'Sending...' : (
                      <>Send Message <ArrowRight size={17} /></>
                    )}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
